import OldDownload from "@iewccouk/MobiusOverrides/Icons/OldDownload";
import downloadDocumentsByType from "@iewccouk/Store/Handlers/Custom/DownloadDocumentsByType";
import { setGA4Event } from "@iewccouk/Store/Handlers/Utilities/CustomTracking";
import { buttonCommon } from "@iewccouk/Utilities/styleguide";
import { colorStrings } from "@iewccouk/Utilities/variables";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getCurrentPage } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    label?: string;
    documentId: string;
    type: string;
    hideIcon?: boolean;
    showAsButton?: boolean;
    customApi?: () => void;
    extendedStyles?: CustomReusableDownloadableStyles;
    overrideFileName?: string;
}

const mapStateToProps = (state: ApplicationState) => {
    return {
        pageTitle: getCurrentPage(state).fields.title,
    };
};

type Props = ReturnType<typeof mapStateToProps> & OwnProps & ResolveThunks<typeof mapDispatchToProps>;

const mapDispatchToProps = {
    downloadDocumentsByType,
};

export interface CustomReusableDownloadableStyles {
    downloadButton?: ButtonPresentationProps;
    downloadClickable?: ClickablePresentationProps;
    downloadIcon?: IconPresentationProps;
    downloadLabel?: TypographyPresentationProps;
}

export const customReusableDownloadableStyles: CustomReusableDownloadableStyles = {
    downloadButton: {
        variant: "primary",
        ...buttonCommon,
    },
    downloadLabel: {
        color: colorStrings.textLink,
    },
    downloadIcon: {
        color: colorStrings.textLink,
        size: 16,
        src: OldDownload,
        css: css`
            margin-right: 5px;
        `,
    },
};

const CustomReusableDownloadable = ({
    label,
    documentId,
    type,
    hideIcon,
    showAsButton,
    customApi,
    extendedStyles,
    overrideFileName,
    downloadDocumentsByType,
    pageTitle,
}: Props) => {
    const [styles] = useState(() => mergeToNew(customReusableDownloadableStyles, extendedStyles));
    const btnLabel = translate(label ?? "Download");

    const handleDownloadDocument = () => {
        if (customApi) {
            customApi();
        } else {
            downloadDocumentsByType({
                documentId,
                type,
                onSuccess: result => {
                    const linkSource = `data:application/pdf;base64,${result.pdF_B64STR}`;
                    const downloadLink = document.createElement("a");
                    const fileName = `${(overrideFileName ?? type).replace(" ", "_")}_${result.salesDocument}.pdf`;
                    downloadLink.href = linkSource;
                    downloadLink.download = fileName;

                    const currentLocation = window.location;

                    setGA4Event([], "sap_file_download", {
                        file_name: fileName,
                        file_extension: fileName.split(".").pop(),
                        link_text: btnLabel,
                        link_url: currentLocation.href,
                        pageTitle,
                    });

                    downloadLink.click();
                },
            });
        }
    };

    const buttonContent = (
        <>
            {!hideIcon && (
                <>
                    <Icon {...styles.downloadIcon} />{" "}
                </>
            )}
            <Typography {...(!showAsButton ? styles.downloadLabel : styles.downloadButton?.typographyProps)}>
                {btnLabel}
            </Typography>
        </>
    );

    if (showAsButton) {
        return (
            <Button
                {...styles.downloadButton}
                data-test-selector={`download_${documentId}`}
                onClick={handleDownloadDocument}
            >
                {buttonContent}
            </Button>
        );
    }

    return (
        <Clickable
            {...styles.downloadClickable}
            data-test-selector={`download_${documentId}`}
            onClick={handleDownloadDocument}
        >
            {buttonContent}
        </Clickable>
    );
};

export default connect(null, mapDispatchToProps)(CustomReusableDownloadable as any) as any;
